import React from 'react';
import classNames from 'classnames';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import { useConfiguration } from '../../context/configurationContext';

import { types as sdkTypes } from '../../util/sdkLoader';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';

import EmptyHeartIcon from './EmptyHeartIcon';
import FullHeartIcon from './FullHeartIcon';

import css from './ListingCard.module.css';

const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType, discountedPrice } = publicData || {};
  const discountedPriceObj = discountedPrice ? new Money( discountedPrice, config.currency ) : null;
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  const { formattedPrice: formattedDiscountedPrice, priceTitle: discountedPriceTitle } =
    priceData( discountedPriceObj, config.currency, intl );
  const discountPercent = price && discountedPrice ?
    Math.round(( price.amount - discountedPrice ) / price.amount * 100 ) : null;

  const priceBlock = discountedPrice ? (
    <>
      <div className={css.priceSlashed} title={priceTitle}>
        <span className={css.slashedLine}>{formattedPrice}</span>
        <span className={css.pricePercent}>-{discountPercent}%</span>
      </div>
      <div className={css.priceValue} title={discountedPriceTitle}>
        {formattedDiscountedPrice}
      </div>
    </>
  ) : (
    <div className={css.priceValue} title={priceTitle}>
      {formattedPrice}
    </div>
  );

  return (
    <div className={css.price}>
      {priceBlock}
    </div>
  );
};

const PRODUCT_LISTING_TYPES = [
  'inquire-products', 'sell-products',
  'book-products-daily', 'book-products-nightly', 'book-products-hourly'
];

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    currentUser,
    onUpdateProfile,
  } = props;

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, description = '', publicData = {}} = currentListing.attributes;
  const { listingType } = publicData;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const favoriteListingIds =
    currentUser && currentUser.attributes.profile.privateData.favoriteListingIds ?
      currentUser.attributes.profile.privateData.favoriteListingIds : [];
  const isFavoriteListing = favoriteListingIds.includes(id);

  const classes = classNames(
    rootClassName || ( PRODUCT_LISTING_TYPES.includes( listingType ) ? css.root : css.sponsorRoot ), className
  );

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const { categoryLevel1 = '', categoryLevel2 = '', categoryLevel3 = ''} = publicData;
  const hidePrice = categoryLevel1.endsWith('___hide_price') ||
    categoryLevel2.endsWith('___hide_price') ||
    categoryLevel3.endsWith('___hide_price');

  const toggleFavorite = e => {
    const newFavoriteListingIds = [...favoriteListingIds];
    const listingIndex = newFavoriteListingIds.indexOf( id );

    if( listingIndex >= 0 ){
      newFavoriteListingIds.splice(listingIndex, 1);
    } else {
      newFavoriteListingIds.push(id);
    }

    e.preventDefault();
    onUpdateProfile({ privateData: { favoriteListingIds: newFavoriteListingIds }});
  };

  const listingInfo = (
    <div className={css.info}>
      <div className={css.price}>
        { !hidePrice ? (
          <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
        ) : null }
        <div className={css.favorite}>
          { typeof window === 'object' ? <ReactTooltip /> : null }
          { isFavoriteListing ? (
            <FullHeartIcon
              onClick={toggleFavorite}
              data-tip={intl.formatMessage({ id: 'ListingCard.fullHeartTooltip'})}
            />
          ) : currentUser ? (
            <EmptyHeartIcon
              onClick={toggleFavorite}
              data-tip={intl.formatMessage({ id: 'ListingCard.emptyHeartTooltip'})}
            />
          ) : null }
        </div>
      </div>
      <div className={css.mainInfo}>
        <div className={css.title}>
          {richText(title, {
            longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
            longWordClass: css.longWord,
          })}
        </div>
        <div className={css.authorInfo}>
          <FormattedMessage id="ListingCard.hostedBy" values={{ authorName }} />
        </div>
      </div>
    </div>
  );

  const sponsorInfo = (
    <>
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
        </div>
      </div>
      <div className={css.contactInfo}>
        <div className={css.mainInfo}>
          <div className={css.description}>
            {description}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
      { PRODUCT_LISTING_TYPES.includes( listingType ) ? listingInfo : sponsorInfo }
    </NamedLink>
  );
};

export default ListingCard;
